import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { Save, X, QrCode, Package, Search } from 'lucide-react';

export default function StockOperationsPanel({ stockData, selectedItem, opLocation, setOpLocation, onComplete }) {
  const [opType, setOpType] = useState('adjust');
  const [opQty, setOpQty] = useState('');
  const [opNotes, setOpNotes] = useState('');
  const [opRef, setOpRef] = useState('');
  const [opDate, setOpDate] = useState(new Date().toISOString().split('T')[0]);
  const [opSubmitting, setOpSubmitting] = useState(false);
  const [issuedTo, setIssuedTo] = useState('');

  // Serial state
  const [serials, setSerials] = useState([]);
  const [serialsLoading, setSerialsLoading] = useState(false);
  const [selectedSerials, setSelectedSerials] = useState(new Set());
  const [serialSearch, setSerialSearch] = useState('');

  const isSerialTracked = stockData?.serial_tracked;
  const isBatchTracked = stockData?.batch_tracked;
  const showSerialPicker = isSerialTracked && (opType === 'issue' || opType === 'return');

  // Load serials when operation type or location changes
  const loadSerials = useCallback(async () => {
    if (!showSerialPicker || !selectedItem) return;
    setSerialsLoading(true);
    try {
      const status = opType === 'issue' ? 'IN_STOCK' : 'ISSUED';
      const params = new URLSearchParams({ status });
      if (opType === 'issue' && opLocation) params.set('location_code', opLocation);
      const r = await api.get(`/api/inventory/serials/by-item/${selectedItem}?${params}`);
      setSerials(r.data.data || []);
    } catch {
      setSerials([]);
    }
    setSerialsLoading(false);
  }, [showSerialPicker, selectedItem, opType, opLocation]);

  useEffect(() => {
    setSelectedSerials(new Set());
    setSerialSearch('');
    loadSerials();
  }, [loadSerials]);

  const toggleSerial = (sn) => {
    setSelectedSerials(prev => {
      const next = new Set(prev);
      if (next.has(sn)) next.delete(sn); else next.add(sn);
      return next;
    });
  };

  const toggleAllSerials = () => {
    const filtered = filteredSerials;
    setSelectedSerials(prev => {
      if (prev.size === filtered.length && filtered.length > 0) return new Set();
      return new Set(filtered.map(s => s.serial_number));
    });
  };

  const filteredSerials = serials.filter(s => {
    if (!serialSearch) return true;
    const q = serialSearch.toLowerCase();
    return s.serial_number?.toLowerCase().includes(q) || s.batch_number?.toLowerCase().includes(q) || s.location_code?.toLowerCase().includes(q);
  });

  const submitOperation = async () => {
    if (showSerialPicker) {
      // Serial-based issue/return
      if (selectedSerials.size === 0) {
        toast.error('Select at least one serial number');
        return;
      }
      setOpSubmitting(true);
      try {
        const endpoint = opType === 'issue' ? '/api/inventory/serials/bulk-issue' : '/api/inventory/serials/bulk-return';
        const payload = {
          serial_numbers: Array.from(selectedSerials),
          reference: opRef,
          issued_to: issuedTo,
          notes: opNotes,
        };
        const r = await api.post(endpoint, payload);
        toast.success(r.data.message);
        setSelectedSerials(new Set());
        setOpNotes('');
        setOpRef('');
        setIssuedTo('');
        onComplete?.();
        loadSerials();
      } catch (e) {
        toast.error(getApiErrorMessage(e, 'Operation failed'));
      }
      setOpSubmitting(false);
    } else {
      // Quantity-based operation
      const qty = parseFloat(opQty);
      if (!qty || !opLocation) {
        toast.error('Quantity and location are required');
        return;
      }
      setOpSubmitting(true);
      try {
        const endpoint = `/api/inventory/stock/${selectedItem}/${opType}`;
        const payload = {
          location_code: opLocation,
          quantity: opType === 'adjust' ? qty : Math.abs(qty),
          notes: opNotes,
          reference: opRef,
          adjustment_date: opDate,
        };
        const r = await api.post(endpoint, payload);
        toast.success(r.data.message);
        setOpQty('');
        setOpNotes('');
        setOpRef('');
        onComplete?.();
      } catch (e) {
        toast.error(getApiErrorMessage(e, 'Operation failed'));
      }
      setOpSubmitting(false);
    }
  };

  return (
    <div className="border rounded-xl p-4" data-testid="stock-operations-panel">
      <h3 className="font-semibold text-sm mb-2">Stock Operations ({opLocation || '—'})</h3>

      {/* Tracking badges */}
      {(isBatchTracked || isSerialTracked) && (
        <div className="flex gap-1.5 mb-3">
          {isBatchTracked && <Badge className="bg-amber-100 text-amber-700 text-[9px]"><Package className="h-2.5 w-2.5 inline mr-0.5" />Batch Tracked</Badge>}
          {isSerialTracked && <Badge className="bg-blue-100 text-blue-700 text-[9px]"><QrCode className="h-2.5 w-2.5 inline mr-0.5" />Serial Tracked</Badge>}
        </div>
      )}

      {/* Operation type tabs */}
      <div className="flex gap-1 mb-3">
        {['adjust', 'issue', 'return'].map(t => (
          <button key={t} onClick={() => setOpType(t)}
            data-testid={`op-type-${t}`}
            className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-colors ${opType === t ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-accent'}`}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {/* Location selector */}
        <div className="space-y-1">
          <Label className="text-xs text-primary font-semibold">Select Location</Label>
          <select value={opLocation} onChange={e => setOpLocation(e.target.value)}
            className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"
            data-testid="op-location-select">
            {(stockData?.locations || []).map(l => (
              <option key={l.location_code} value={l.location_code}>{l.location_code} ({l.current_stock} in stock)</option>
            ))}
          </select>
        </div>

        {/* SERIAL PICKER for issue/return of serial items */}
        {showSerialPicker ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-primary font-semibold">
                {opType === 'issue' ? 'Select Serial Numbers to Issue' : 'Select Serial Numbers to Return'}
              </Label>
              <span className="text-[10px] text-muted-foreground font-semibold">
                {selectedSerials.size} selected of {filteredSerials.length}
              </span>
            </div>

            {/* Serial search */}
            <div className="relative">
              <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={serialSearch} onChange={e => setSerialSearch(e.target.value)}
                placeholder="Search serials..." className="pl-7 h-7 text-xs" data-testid="serial-search" />
            </div>

            {serialsLoading ? (
              <p className="text-xs text-muted-foreground animate-pulse py-2">Loading serials...</p>
            ) : filteredSerials.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">
                {opType === 'issue' ? 'No available serials at this location.' : 'No issued serials found for this item.'}
              </p>
            ) : (
              <div className="border rounded-lg max-h-[220px] overflow-y-auto">
                {/* Select all */}
                <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/50 border-b sticky top-0">
                  <input type="checkbox"
                    className="h-3 w-3 rounded accent-primary cursor-pointer"
                    checked={selectedSerials.size === filteredSerials.length && filteredSerials.length > 0}
                    onChange={toggleAllSerials}
                    data-testid="serial-select-all" />
                  <span className="text-[10px] font-semibold text-muted-foreground cursor-pointer" onClick={toggleAllSerials}>
                    Select All ({filteredSerials.length})
                  </span>
                </div>
                {filteredSerials.map(s => (
                  <div key={s.serial_number}
                    className={`flex items-center gap-2 px-2 py-1 border-b last:border-b-0 cursor-pointer hover:bg-accent/20 transition-colors ${selectedSerials.has(s.serial_number) ? 'bg-primary/5' : ''}`}
                    onClick={() => toggleSerial(s.serial_number)}
                    data-testid={`serial-row-${s.serial_number}`}>
                    <input type="checkbox"
                      className="h-3 w-3 rounded accent-primary cursor-pointer"
                      checked={selectedSerials.has(s.serial_number)}
                      onChange={() => toggleSerial(s.serial_number)}
                      onClick={e => e.stopPropagation()} />
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] font-mono font-semibold text-primary">{s.serial_number}</span>
                      {s.batch_number && (
                        <span className="ml-1.5 text-[9px] text-muted-foreground">({s.batch_number})</span>
                      )}
                    </div>
                    <span className="text-[9px] text-muted-foreground">{s.location_code}</span>
                    {opType === 'return' && s.issued_to && (
                      <span className="text-[9px] text-amber-600 truncate max-w-[60px]">{s.issued_to}</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Issued To (for issue only) */}
            {opType === 'issue' && (
              <div className="space-y-1">
                <Label className="text-xs">Issued To</Label>
                <Input value={issuedTo} onChange={e => setIssuedTo(e.target.value)}
                  className="h-7 text-xs" placeholder="Person or department"
                  data-testid="issued-to-input" />
              </div>
            )}

            {/* Reference */}
            <div className="space-y-1">
              <Label className="text-xs">Reference</Label>
              <Input value={opRef} onChange={e => setOpRef(e.target.value)}
                className="h-7 text-xs" placeholder="e.g. REQ000001"
                data-testid="op-reference" />
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label className="text-xs">Comments</Label>
              <Textarea value={opNotes} onChange={e => setOpNotes(e.target.value)} rows={2} className="text-xs" />
            </div>

            <Button size="sm" onClick={submitOperation}
              disabled={opSubmitting || selectedSerials.size === 0}
              data-testid="serial-submit-btn"
              className="w-full">
              <Save className="h-3.5 w-3.5 mr-1" />
              {opSubmitting ? 'Processing...' : `${opType === 'issue' ? 'Issue' : 'Return'} ${selectedSerials.size} Serial(s)`}
            </Button>
          </div>
        ) : (
          /* STANDARD quantity-based operations */
          <>
            {opType === 'adjust' && (
              <div className="space-y-1">
                <Label className="text-xs text-primary font-semibold">Adjustment Date</Label>
                <Input type="date" value={opDate} onChange={e => setOpDate(e.target.value)} className="h-8 text-sm" />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-primary font-semibold">
                {opType === 'adjust' ? 'Quantity to Adjust' : opType === 'issue' ? 'Quantity to Issue' : 'Quantity to Return'}
              </Label>
              <Input type="number" step="0.01" value={opQty} onChange={e => setOpQty(e.target.value)}
                className="h-8 text-sm" placeholder={opType === 'adjust' ? 'Positive or negative' : 'Enter quantity'}
                data-testid="op-qty-input" />
            </div>
            {(opType === 'issue' || opType === 'return') && (
              <div className="space-y-1">
                <Label className="text-xs">Reference</Label>
                <Input value={opRef} onChange={e => setOpRef(e.target.value)} className="h-8 text-sm" placeholder="e.g. REQ000001" />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Comments</Label>
              <Textarea value={opNotes} onChange={e => setOpNotes(e.target.value)} rows={2} className="text-sm" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={submitOperation} disabled={opSubmitting} data-testid="op-submit-btn">
                <Save className="h-3.5 w-3.5 mr-1" /> {opSubmitting ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setOpQty(''); setOpNotes(''); setOpRef(''); }}>
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
